import { faroCoreApiClient } from "@api/faro-core-api-client";
import { DEFAULT_PROJECTS_SORT_BY } from "@components/projects/projects-sort-constants";
import { SdbProject } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  FetchProjectsProps,
  ProjectsList,
  ProjectsListType,
  ProjectTypes,
  SdbSearchedProject,
  UpdateMemberRoleInProject,
} from "@store/projects/projects-slice-types";
import { isValidProjectSearchQuery } from "@utils/project-utils";
import { HttpUtils } from "@stellar/utils";
import { AuthorizationError } from "@faro-lotv/service-wires";

const AUTHORIZATION_ERROR_NAME = "AuthorizationError";

/** Stores how many projects are returned from the backend per request. */
export const API_PROJECTS_PER_REQUEST = 30;

/** Initial state of a list of projects */
export const PROJECTS_LIST_INITIAL_STATE: ProjectsList = {
  ids: [],
  hasFetchedFirstPage: false,
  totalCount: 0,
};

/** Converts ProjectTypes to SdbProject */
export function convertProjectTypesToSdbProject(
  project: ProjectTypes
): SdbProject {
  return {
    accessLevel: project.accessLevel,

    address: project.address,

    archivingState:
      "archivingState" in project ? project.archivingState : project.state,

    area:
      "area" in project
        ? project.area
        : {
            amount: project.sqftUsed,
            unit: "sqft",
          },

    clientName: getProjectClientName(project),

    createdAt: project.createdAt,

    description: project.description ?? null,

    evaluation: "evaluation" in project ? project.evaluation : null,

    features: "features" in project ? project.features : null,

    featuresAvailable:
      "featuresAvailable" in project ? project.featuresAvailable : null,

    fileUrl: "fileUrl" in project ? project.fileUrl : null,

    group: getProjectGroup(project),

    id: project.id,

    lastCapturedAt: project.lastCapturedAt ?? null,

    lastDownloadedAt: project.lastDownloadedAt ?? null,

    managers: getProjectManagers(project),

    members: "members" in project && project.members ? project.members : [],

    modifiedAt: project.modifiedAt,

    name: project.name,

    permissions: "permissions" in project ? project.permissions : [],

    role: "role" in project && project.role ? project.role : null,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- coming from backend
    standalone: "standalone" in project ? project.standalone : null,

    tags: project.tags,

    thumbnailUrl: project.thumbnailUrl ?? null,

    storage: project.storage ?? { size: 0 },

    position: project.position,

    snapshots: [],
  };
}

interface RestoreMissingProjectFields {
  /** The updated project data from the API response */
  updatedProject: SdbProject;

  /** The existing project data from Redux store before updating */
  existingProject: SdbProject | undefined;
}

/**
 * Restores missing fields to an updated project from the existing project.
 * Takes updated project data and restores fields that weren't included in the API response
 * but are needed for UI functionality.
 * @returns Updated project with missing fields restored
 */
export function restoreMissingProjectFields({
  updatedProject,
  existingProject,
}: RestoreMissingProjectFields): SdbProject {
  if (!existingProject) {
    return updatedProject;
  }

  return {
    ...updatedProject,
    // Restore featuresAvailable since updateProjectDetails API doesn't return it
    featuresAvailable: existingProject.featuresAvailable,
  };
}

/** Returns the project's client name */
function getProjectClientName(project: ProjectTypes): SdbProject["clientName"] {
  if ("clientName" in project && project.clientName) {
    return project.clientName;
  } else if ("client" in project && project.client) {
    return project.client;
  }
  return null;
}

/** Converts the types of featureAvailable to features */
export function convertFeaturesAvailableToFeatures(
  featuresAvailable: FaroCoreApiTypes.Feature[]
): FaroCoreApiTypes.FeatureFlags {
  return featuresAvailable.reduce((acc, feature) => {
    /* eslint-disable @typescript-eslint/naming-convention -- naming defined by backend */
    return { [feature.identifier]: { enabled: feature.enabled }, ...acc };
  }, {} as FaroCoreApiTypes.FeatureFlags);
}

/**
 * Update a member role in the project using the backend.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export async function updateMemberRoleInProjectBase({
  companyId,
  projectId,
  userIdentity,
  payload,
}: FaroCoreApiTypes.UpdateMemberRoleInProjectProps): Promise<UpdateMemberRoleInProject> {
  await faroCoreApiClient.projects.updateMemberRoleInProject({
    companyId,
    projectId,
    userIdentity,
    payload,
  });

  return {
    projectId,
    role: payload.role,
    userIdentity,
  };
}

/** Whether the project entity is of type ICompanyProject with the group property defined */
export function isICompanyProject(
  project: ProjectTypes
): project is FaroCoreApiTypes.CompanyProject {
  return "group" in project && typeof project.group === "object";
}

/** Whether the project entity is of type SdbSearchedProject with the group property defined */
export function isSdbSearchedProject(
  project: ProjectTypes
): project is SdbSearchedProject {
  return (
    "context" in project &&
    "group" in project.context &&
    typeof project.context.group === "object"
  );
}

/** Returns the group property from the passed ProjectTypes entity. If group is not defined it returns null */
export function getProjectGroup(project: ProjectTypes): SdbProject["group"] {
  if (isSdbSearchedProject(project)) {
    return {
      id: project.context.group.id,
      name: project.context.group.name,
    };
  }

  if (isICompanyProject(project)) {
    return {
      id: project.group.id,
      name: project.group.name,
    };
  }

  return null;
}

/**
 * @returns The project managers object with the project and company manager
 * @param project the project entity from which the managers should be extracted
 */
export function getProjectManagers(
  project: ProjectTypes
): SdbProject["managers"] {
  if ("managers" in project && project.managers) {
    return {
      projectManager: project.managers.projectManager ?? null,
      companyManager: project.managers.companyManager ?? null,
    };
  }

  return {
    projectManager: null,
    companyManager: null,
  };
}

/**
 * @returns the project list type based on the provided parameters
 * If any of the query params are defined (search, sortBy, groupId), then "transient" is returned,
 * otherwise the current projectArchivingState is returned
 * @param projectArchivingState current project archiving state
 * @param search current search query if defined
 * @param sortBy current sorting option if defined
 * @param groupId ID of the group to query the projects
 */
export function getListType({
  projectArchivingState,
  search,
  sortBy,
  groupId,
}: FetchProjectsProps): ProjectsListType {
  if (
    (search !== undefined && isValidProjectSearchQuery(search)) ||
    (sortBy !== undefined && sortBy !== DEFAULT_PROJECTS_SORT_BY) ||
    groupId !== undefined
  ) {
    return "transient";
  }

  return projectArchivingState;
}

/**
 * Converts the projects response data from the Core API client to SdbSearchedProject entities
 * @returns the list of converted SdbSearchedProject entities
 * @param data the data property from the GetProjectsResponse returned by the Core API client
 */
function convertProjectsResponseToSdbSearchedProjects(
  data: FaroCoreApiTypes.GetProjectsResponse["data"]
): SdbSearchedProject[] {
  return data.map((entity) => {
    return {
      ...entity.project,
      features: convertFeaturesAvailableToFeatures(entity.featuresAvailable),
      featuresAvailable: entity.featuresAvailable,
      managers: {
        projectManager: entity.context.projectManager,
      },
      context: entity.context,
      permissions: entity.permissions,
    };
  });
}

/**
 * Converts the projects response data from the Core API client to SdbProject entities
 * @returns the list of converted SdbProject entities
 * @param data the data property from the GetProjectsResponse returned by the Core API client
 */
export function convertProjectsResponseToSdbProjects(
  data: FaroCoreApiTypes.GetProjectsResponse["data"]
): SdbProject[] {
  const sdbSearchedProjects =
    convertProjectsResponseToSdbSearchedProjects(data);
  return sdbSearchedProjects.map(convertProjectTypesToSdbProject);
}

/**
 * Checks if an error corresponds to a forbidden project settings error.
 *
 * A forbidden project settings error occurs when the user does not have the necessary
 * permissions to access or modify the project settings. This can happen in the following cases:
 * - The API returns a 403 Forbidden status code.
 * - The error is an instance of AuthorizationError, which indicates a lack of authorization.
 * - The error is a generic Error with the name 'AuthorizationError'.
 *
 * @param error The error object to check.
 * @returns True if the error is a forbidden project settings error, false otherwise.
 */
export function isForbiddenProjectSettingsError(error: unknown): boolean {
  if (HttpUtils.getErrorStatus(error) === HttpUtils.HTTP_ERROR_CODE.Forbidden) {
    return true;
  }

  if (error instanceof AuthorizationError) {
    return true;
  }

  if (error instanceof Error && error.name === AUTHORIZATION_ERROR_NAME) {
    return true;
  }

  return false;
}
